import { useEffect, useRef, type ReactNode } from 'react';
import { Tooltip } from './Tooltip';

export interface TabItem {
  id: string;
  label: ReactNode;
  /**
   * What to show in the tab's badge — the app's standard filled pill, not a bare
   * digit. (b-01929)
   *
   * A single number renders as "3". A PAIR renders as "1/7", for a tab whose
   * contents are two different quantities that both matter — the Feedback tab
   * counts boards AND the threads still open across them, and either alone is
   * misleading: "3" hides forty unanswered comments, "40" hides that they are
   * spread over three boards.
   *
   * Lives on the primitive rather than in each caller's label so every tab counts
   * the same way — the two Feedback tabs had drifted into counting different
   * things behind identical badges.
   *
   * A falsy single count is omitted (no badge beats a zero); a pair is omitted
   * only when its FIRST number is zero, so "1/0" still says "there is a board, and
   * nothing outstanding on it".
   */
  count?: number | readonly [number, number];
  /** What the badge means, for assistive technology — e.g. "1 board, 7 open
   *  comments". Without it a screen reader announces a bare "1/7". */
  countLabel?: string;
  /** A house Tooltip (hover, keyboard focus, long-press) for a tab whose label
   *  is an icon alone. The label must already carry the accessible name, so the
   *  tooltip does not describe the tab a second time. (f-02064) */
  tooltip?: string;
}

/** Shared tab strip — underline style, 44px touch targets, scrollable on
 *  compact (see .tabs-* in responsive.css). Use for page tabs + Insights.
 *  `flush` drops the full-width baseline hairline under the strip (the active
 *  tab keeps its own underline) for pages that don't want the divider. */
/** The badge. Its own component so the "when is it shown" rule lives in one
 *  place rather than inline in the map. */
function TabCount({ count, label }: { count?: number | readonly [number, number]; label?: string }) {
  if (count == null) return null;
  const cap = (n: number) => (n > 99 ? '99+' : String(n));
  const shown = typeof count === 'number'
    ? (count ? cap(count) : null)
    : (count[0] ? `${cap(count[0])}/${cap(count[1])}` : null);
  if (!shown) return null;
  return (
    <span className="tabs-count" aria-label={label} title={label}>{shown}</span>
  );
}

export function Tabs({ tabs, active, onChange, flush = false }: {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
  flush?: boolean;
}) {
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the active tab in view as `active` changes (e.g. the setup wizard stepper),
  // so the current step always "follows along" and its full label stays visible.
  // Scrolls the strip horizontally ONLY (computed from bounding rects so it never
  // scrolls an ancestor or the page); honours prefers-reduced-motion.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const el = strip.querySelector('[aria-selected="true"]') as HTMLElement | null;
    if (!el) return;
    const stripRect = strip.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const target = strip.scrollLeft + (elRect.left - stripRect.left) - strip.clientWidth / 2 + el.clientWidth / 2;
    const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollTo({ left: Math.max(0, target), behavior: reduce ? 'auto' : 'smooth' });
  }, [active]);

  return (
    <div ref={stripRef} className={`tabs-strip${flush ? ' tabs-flush' : ''}`} role="tablist">
      {tabs.map((t) => {
        const tab = (
          <button
            key={t.id}
            role="tab"
            aria-selected={active === t.id}
            className={`tabs-tab ${active === t.id ? 'active' : ''}`}
            onClick={() => onChange(t.id)}
          >
            {t.label}
            <TabCount count={t.count} label={t.countLabel} />
          </button>
        );
        return t.tooltip ? <Tooltip key={t.id} content={t.tooltip} describe={false}>{tab}</Tooltip> : tab;
      })}
    </div>
  );
}
